import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function MoveReservationDialog({ reservation, tripId, isOnlyReservation, onClose, onMoved }) {
  const [trips, setTrips] = useState(null);
  const [mode, setMode] = useState("existing"); // existing | new
  const [targetId, setTargetId] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/trips").then(({ data }) => {
      const others = data.filter((t) => t.id !== tripId);
      setTrips(others);
      if (others.length > 0) setTargetId(others[0].id);
      else setMode("new");
    }).catch(() => { setTrips([]); toast.error("Could not load your trips"); });
  }, [tripId]);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "existing") {
        const { data } = await api.post(`/trips/${tripId}/reservations/${reservation.id}/move`, {
          target_trip_id: targetId,
        });
        toast.success("Reservation moved");
        onMoved({ tripId: data.trip_id, sourceDeleted: data.source_trip_deleted });
      } else {
        const { data } = await api.post(`/trips/${tripId}/split`, {
          reservation_ids: [reservation.id],
          new_trip_name: newName.trim() || null,
        });
        toast.success("Moved to new trip");
        onMoved({ tripId: data.new_trip.id, sourceDeleted: false });
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Move failed");
      setBusy(false);
    }
  };

  const canSubmit = mode === "existing" ? !!targetId : !isOnlyReservation;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-sand-300" data-testid="move-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight">
            Move reservation
          </DialogTitle>
        </DialogHeader>

        {trips === null ? (
          <div className="h-24 bg-sand-50 animate-pulse" />
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => setMode("existing")}
                disabled={trips.length === 0}
                className={`btn-swiss text-xs flex-1 justify-center ${mode === "existing" ? "btn-primary" : "btn-ghost"} disabled:opacity-40`}
                data-testid="move-mode-existing"
              >
                Existing trip
              </button>
              <button
                onClick={() => setMode("new")}
                className={`btn-swiss text-xs flex-1 justify-center ${mode === "new" ? "btn-primary" : "btn-ghost"}`}
                data-testid="move-mode-new"
              >
                New trip
              </button>
            </div>

            {mode === "existing" ? (
              <div>
                <label className="label-tag block mb-2">Destination trip</label>
                <select
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className="swiss-input w-full text-sm py-2"
                  data-testid="move-target-select"
                >
                  {trips.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.start_date ? ` · ${t.start_date}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : isOnlyReservation ? (
              <p className="text-xs text-[#8A5F22] bg-[#F3E7D3] border border-[#E9D5AE] px-3 py-2" data-testid="move-only-res-hint">
                This is the trip's only reservation — moving it to a new trip would just rename this one.
                Use "Edit trip" instead, or move it to an existing trip.
              </p>
            ) : (
              <div>
                <label className="label-tag block mb-2">New trip name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Return leg"
                  className="swiss-input w-full text-sm py-2"
                  data-testid="move-new-name-input"
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="move-cancel-btn">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !canSubmit}
            className="btn-swiss btn-primary text-sm disabled:opacity-50"
            data-testid="move-confirm-btn"
          >
            {busy ? "Moving…" : "Move"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
